import { Locale, translate } from "./translations";

const en: Locale = {
  welcome: "Welcome to Apaato",
  signIn: "Sign In",
  register: "Register",
  accountType: "Account type",
  accountTypeUser: "User",
  accountTypePartner: "Partner",
  firstName: "First name",
  lastName: "Last name",
  orgName: "Organization name",
  createAccount: "Create account",
  registrationRequired: "Please complete registration to continue.",
  signOut: "Sign Out",
  greeting: (name: string) => `Hello, ${name}`,
  email: "Email",
  password: "Password",
  continueWithGoogle: "Continue with Google",
  pleaseWait: "Please wait...",
  fillAllFields: "Please fill out all fields.",
  userNotFound: "No user found with this email.",
  wrongPassword: "Incorrect password.",
  authFailed: "Authentication failed. Please try again.",
  signInCancelled: "Sign-in process was cancelled.",
  googleSignInFailed: "Google sign-in failed. Please try again.",
  notAuthorized: "Your account is not authorized to access this environment.",
  registrationFailed: "Registration failed. Please try again.",

  confirmPassword: "Confirm password",
  accountTypeUserHint: "I want to find an apartment in Japan",
  accountTypePartnerHint: "I want to post housing ads",
  partnerType: "Partner type",
  partnerTypeIndividual: "Individual",
  partnerTypeIndividualHint: "I work independently",
  partnerTypeOrganization: "Organization",
  partnerTypeOrganizationHint: "I'm part of a company",
  show: "Show",
  hide: "Hide",
  emailRequired: "Email is required",
  emailInvalid: "Please enter a valid email address",
  passwordRequired: "Password is required",
  passwordMin8: "Password must be at least 8 characters",
  passwordNeedNumber: "Password must contain at least 1 number",
  passwordNeedSymbol: "Password must contain at least 1 symbol",
  passwordNeedUpper: "Password must contain at least 1 uppercase letter",
  passwordNeedLower: "Password must contain at least 1 lowercase letter",

  passwordRequirements: "Password requirements",
  passwordReqMin8: "At least 8 characters",
  passwordReqNumber: "At least 1 number",
  passwordReqSymbol: "At least 1 symbol",
  passwordReqUpper: "At least 1 uppercase letter",
  passwordReqLower: "At least 1 lowercase letter",

  notVerified: "Please verify your email before signing in.",
  forbidden: "You are not allowed to access this environment.",

  confirmPasswordRequired: "Confirm your password",
  passwordsDontMatch: "Passwords do not match",
  firstNameRequired: "First name is required",
  lastNameRequired: "Last name is required",
  orgNameRequired: "Organization name is required",

  // Email verification flow
  verificationSentTitle: "Verify your email",
  verificationSentBody: (email: string) =>
    `We sent a verification email to ${email}. Please check your inbox and follow the link to verify your account.`,
  okGotIt: "OK, got it",
  emailConfirmedTitle: "Email confirmed",
  emailConfirmedBody:
    "Thank you! Your email is verified. You can now log into your account.",
  logIntoAccount: "Log into account",

  // Verification page
  verificationFailedTitle: "Verification failed",
  verificationExpired:
    "This verification link has expired. Please request a new one.",
  verificationInvalid:
    "This verification link is invalid or has already been used.",
  verificationFailed: "Unable to verify your email. Please try again later.",
  verificationMissingCodeTitle: "Missing verification code",
  verificationMissingCodeBody:
    "No verification code was provided. Please click the link from your email again.",
  backToLogin: "Back to sign in",
  resendVerificationEmail: "Resend verification email",
  resendVerificationHint: (email: string) =>
    `Would you like us to send a new verification link to ${email}?`,
  verificationResent:
    "A new verification email has been sent. Please check your inbox.",
  resendFailed: "Failed to send verification email. Please try again.",

  // Homepage
  dashboard: "Dashboard",
  homeHeroTitle: "Find your perfect home in Japan",
  homeHeroSubtitle:
    "Discover quality apartments in Tokyo, curated for expats and locals alike.",
  searchPrefecture: "Prefecture",
  searchSelectPrefecture: "Select prefecture",
  searchCity: "City",
  searchSelectCity: "Select city",
  searchLocation: "Location",
  searchSelectArea: "Select area",
  searchMoveIn: "Move-in date",
  searchMoveInPlaceholder: "Select date",
  searchOccupants: "Occupants",
  searchRentBudget: "Monthly budget",
  searchRoomLayout: "Room layout",
  searchPerson: "person",
  searchPeople: "people",
  searchButton: "Search apartments",
  searchEditCriteria: "Edit search",
  featureQualityTitle: "Quality homes",
  featureQualityDesc: "Every listing is verified for quality and comfort.",
  featureTrustedTitle: "Trusted partners",
  featureTrustedDesc: "Work with verified agencies and landlords.",
  featureSupportTitle: "Local support",
  featureSupportDesc: "Multilingual team ready to help you settle in.",
  disclaimerTitle: "MVP in Progress",
  disclaimerBody:
    "This is an early MVP version. Search functionality and listings are coming soon. Currently only Tokyo area will be supported.",
  footerRights: "All rights reserved.",

  // Philosophy Section
  philosophyTitle: "Our Philosophy",
  philosophySubtitle:
    "We want to simplify apartment hunting in Japan for foreigners.",
  philosophyNoFeesTitle: "No Hidden Fees",
  philosophyNoFeesDesc:
    "Complete price transparency. What you see is what you pay — no surprises.",
  philosophySimpleTitle: "Simple Interface",
  philosophySimpleDesc:
    "Clean, intuitive design that makes finding your next home a breeze.",
  philosophyQualityTitle: "Quality Listings",
  philosophyQualityDesc:
    "High-quality listings with all required details about property and apartments.",
  philosophyVerifiedTitle: "Verified Realtors",
  philosophyVerifiedDesc:
    "Work only with trusted, verified real estate professionals.",

  // Snackbar messages
  welcomeUserSnackbar: "Welcome! Dashboard features are coming soon.",
  welcomeAdminSnackbar: "Welcome back, admin!",

  // Dashboard
  dashboardWelcome: "Welcome back",
  dashboardSubtitle: "Manage your apartment search and saved listings.",
  dashboardSearch: "Search",
  dashboardSearchDesc: "Find apartments in Tokyo",
  dashboardFavorites: "Favorites",
  dashboardFavoritesDesc: "View saved listings",
  dashboardNotifications: "Notifications",
  dashboardNotificationsDesc: "Updates on your searches",
  dashboardSettings: "Settings",
  dashboardSettingsDesc: "Manage your account",
  dashboardComingSoon: "Features Coming Soon",
  dashboardComingSoonDesc:
    "These features are currently under development. Check back soon!",
  dashboardDevTools: "Developer Tools",
  dashboardDevToolsDesc: "API access for testing and development",
  dashboardTokenInfo: "Use your ID token to authenticate API requests in",
  dashboardShowToken: "Show Token",
  dashboardCopyToken: "Copy Token",
  dashboardCopied: "Copied!",
  dashboardTokenError: "Unable to get token. Try refreshing the page.",

  // Profile Page
  profileWelcome: "Welcome back",
  profileSubtitle: "Manage your profile, listings, and preferences.",
  profileSearch: "Search",
  profileSearchDesc: "Find apartments in Tokyo",
  profileMyListings: "My Listings",
  profileMyListingsDesc: "Manage your published listings",
  profileFavorites: "Favorites",
  profileFavoritesDesc: "View saved listings",
  profileNotifications: "Notifications",
  profileNotificationsDesc: "Updates on your account",
  profileSettings: "Settings",
  profileSettingsDesc: "Manage your account",
  profileComingSoon: "Features Coming Soon",
  profileComingSoonDesc:
    "Favorites, notifications, and settings are currently under development.",
  profileCreateListing: "New Listing",

  // Overview Page
  back: "Back",
  overviewSearchingIn: "Searching in",
  overviewListView: "List",
  overviewMapView: "Map",
  overviewResultsTitle: "Search Results",
  overviewNoResults: "0 apartments found",
  overviewEmptyTitle: "No apartments found",
  overviewEmptyDesc:
    "Adjust your filters or search criteria to find more apartments. Data will be populated soon.",
  overviewMapTitle: "Map View",
  overviewMapComingSoon: "Map View Coming Soon",
  overviewMapComingSoonDesc:
    "Interactive map with apartment locations will be available in a future update.",
  overviewLoading: "Searching apartments...",
  overviewResults: "apartments found",
  overviewErrorTitle: "Something went wrong",
  overviewRetry: "Try again",
  overviewLoadingMore: "Loading more...",
  overviewLoadMore: "Load more",

  // Listing Card
  "listings.min": "min",
  "listings.month": "mo",
  "listings.foreignerFriendly": "Foreigner OK",
  "listings.petsAllowed": "Pets OK",
  "listings.airConditioning": "AC",
  "listings.deposit": "Deposit",
  "listings.keyMoney": "Key",

  // Filters
  filtersTitle: "Filters",
  filterReset: "Reset",

  // Filter Categories
  filterCategoryProperty: "Property Type",
  filterCategoryPrice: "Price & Fees",
  filterCategorySize: "Size & Layout",
  filterCategoryAmenities: "Amenities",
  filterCategoryBuilding: "Building",
  filterCategoryAccess: "Access",
  filterCategoryContract: "Contract",

  // Property Types
  filterPropertyType: "Property type",
  filterPropertyApartment: "Apartment",
  filterPropertyMansion: "Mansion",
  filterPropertyShareHouse: "Share House",
  filterPropertyGuestHouse: "Guest House",
  filterPropertyMonthlyMansion: "Monthly Mansion",

  // Room Layouts
  filterRoomLayout: "Room layout",
  filterLayout1R: "1R",
  filterLayout1K: "1K",
  filterLayout1DK: "1DK",
  filterLayout1LDK: "1LDK",
  filterLayout2K: "2K",
  filterLayout2DK: "2DK",
  filterLayout2LDK: "2LDK",
  filterLayout3K: "3K",
  filterLayout3DK: "3DK",
  filterLayout3LDKPlus: "3LDK+",

  // Price Filters
  filterMinRent: "Min rent",
  filterMaxRent: "Max rent",
  filterIncludeManagementFee: "Include management fee",
  filterKeyMoney: "Key money",
  filterDeposit: "Deposit",
  filterGuarantor: "Guarantor required",
  filterNoKeyMoney: "No key money",
  filterNoDeposit: "No deposit",
  filterHalfMonth: "0.5 month",
  filter1Month: "1 month",
  filter2Months: "2 months",
  filterAny: "Any",
  filterYes: "Yes",
  filterNo: "No",

  // Size Filters
  filterMinSize: "Min size",
  filterMaxSize: "Max size",

  // Amenities
  filterAirConditioning: "Air conditioning",
  filterWasherHookup: "Washer hookup",
  filterWasherDryerInUnit: "Washer/dryer in-unit",
  filterBathToiletSeparate: "Bath/toilet separate",
  filterBalcony: "Balcony",
  filterFlooringWood: "Wood flooring",
  filterInternetIncluded: "Internet included",
  filterGasStove: "Gas stove",
  filterIHCooktop: "IH cooktop",

  // Building
  filterBuildingAge: "Building age",
  filterAnyAge: "Any age",
  filterUnder5Years: "Under 5 years",
  filterUnder10Years: "Under 10 years",
  filterUnder20Years: "Under 20 years",
  filterUnder30Years: "Under 30 years",
  filterFloor: "Floor",
  filterAnyFloor: "Any floor",
  filterFirstFloor: "1st floor",
  filter2ndFloorPlus: "2nd floor+",
  filter3rdFloorPlus: "3rd floor+",
  filterTopFloor: "Top floor",
  filterElevator: "Elevator",
  filterAutoLock: "Auto-lock entrance",
  filterDeliveryBox: "Delivery box",
  filterParking: "Parking available",
  filterBicycleParking: "Bicycle parking",

  // Access
  filterWalkToStation: "Walk to station",
  filterAnyWalkTime: "Any distance",
  filterWithin5Min: "Within 5 min",
  filterWithin10Min: "Within 10 min",
  filterWithin15Min: "Within 15 min",
  filterWithin20Min: "Within 20 min",

  // New categories
  filterCategoryBathroom: "Bathroom & Laundry",
  filterCategoryKitchen: "Kitchen",
  filterCategorySecurity: "Security",
  filterCategoryListing: "Listing",

  // Bathroom & Laundry
  filterIndoorWasherSpace: "Indoor washer space",
  filterReheatingBath: "Reheating bath",
  filterBathroomDryer: "Bathroom dryer",
  filterWashletSeat: "Washlet/bidet seat",
  filterIndependentVanity: "Independent vanity",

  // Kitchen
  filterSystemKitchen: "System kitchen",
  filterCounterKitchen: "Counter kitchen",
  filterDishwasher: "Dishwasher",

  // Building extras
  filterSouthFacing: "South-facing",
  filterCornerUnit: "Corner unit",
  filterBuildingStructure: "Building structure",
  filterStructureRC: "RC (Reinforced Concrete)",
  filterStructureSteel: "Steel",
  filterStructureWood: "Wooden",
  filterStructureSRC: "SRC",

  // Security
  filterSecurityCamera: "Security camera",
  filterVideoIntercom: "Video intercom",

  // Parking extras
  filterMotorcycleParking: "Motorcycle parking",

  // Contract
  filterForeignerFriendly: "Foreigner-friendly",
  filterPetsAllowed: "Pets allowed",
  filterInstrumentsOK: "Musical instruments OK",
  filterSohoOK: "SOHO/Office OK",
  filterNoGuarantorNeeded: "No guarantor needed",
  filterMoveInWithin30Days: "Move-in within 30 days",

  // Listing freshness
  filterListingFreshness: "Listing freshness",
  filterFreshnessAny: "Any time",
  filterFreshness24h: "Last 24 hours",
  filterFreshness3d: "Last 3 days",
  filterFreshness7d: "Last 7 days",
  filterFreshness14d: "Last 14 days",

  // Theme switcher
  themeSwitchToLight: "Switch to light mode",
  themeSwitchToDark: "Switch to dark mode",

  // User Card
  profileIndividual: "Individual Partner",
  backToProfile: "Back to Profile",

  // Create/Edit Listing Page
  createListingTitle: "Create New Listing",
  editListingTitle: "Edit Listing",
  createListingSubtitle: "Fill in the details to publish your property listing",
  createListingPartnerOnly: "Only partners can create listings",
  createListingBasicInfo: "Basic Information",
  createListingTitleField: "Listing title",
  createListingTitlePlaceholder: "e.g., Spacious 1LDK near Shibuya Station",
  createListingDescription: "Description",
  createListingDescPlaceholder:
    "Describe the property features, amenities, and surrounding area...",
  createListingLocation: "Location",
  createListingCityPlaceholder: "e.g., Tokyo",
  createListingWard: "Ward/District",
  createListingAddress: "Address",
  createListingStation: "Nearest station",
  createListingStationPlaceholder: "e.g., Shibuya Station",
  createListingWalkMinutes: "Walk to station (min)",
  createListingPricing: "Pricing",
  createListingPrice: "Monthly rent (¥)",
  createListingManagementFee: "Management fee (¥)",
  createListingBuilding: "Building Details",
  createListingArea: "Area (m²)",
  createListingTotalFloors: "Total floors",
  createListingYearBuilt: "Year built",
  createListingAvailableFrom: "Available from",
  createListingPhotos: "Photos",
  createListingPhotosHelp:
    "Upload photos for each section. Minimum 5 images required to publish.",
  createListingPhotosComingSoon: "Photo upload coming soon",
  createListingImageUploadError: "Failed to upload images",
  createListingSaveDraft: "Save Draft",
  createListingPublish: "Publish",
  createListingSaved: "Listing saved as draft",
  createListingPublished: "Listing published successfully",
  createListingError: "Failed to create listing. Please try again.",
  createListingValidationError: "Please fill in all required fields",
  validationErrors: "Please fix the following errors:",
  createListingTitleRequired: "Title is required",
  createListingTitleMinLength: "Title must be at least 5 characters",
  createListingPropertyTypeRequired: "Property type is required",
  createListingRoomLayoutRequired: "Room layout is required",
  createListingPrefectureRequired: "Prefecture is required",
  createListingCityRequired: "City is required",
  createListingPriceRequired: "Price must be greater than 0",
  createListingMinImagesRequired: "Minimum 5 images required to publish",
  createListingAddressHint:
    "Enter the full address, including building name and apartment number.",
  createListingInvalidAddress: "Invalid address",
  createListingAddressValidationError: "Error validating address",
  createListingValidatingAddress: "Validating address...",
  createListingValidating: "Validating...",
  createListingValidate: "Validate Address",

  // Find Nearby Stations
  findNearbyStations: "Find Nearby Stations",

  // My Listings Page
  myListingsTitle: "My Listings",
  myListingsSubtitle: "Manage your published and draft listings",
  myListingsPublishedTab: "Published & History",
  myListingsDraftsTab: "Drafts",
  myListingsEmpty: "No listings yet",
  myListingsEmptyDesc: "Start by creating your first property listing",
  myListingsEmptyPublished: "No published listings",
  myListingsEmptyPublishedDesc:
    "Publish a draft or create a new listing to get started.",
  myListingsEmptyDrafts: "No draft listings",
  myListingsEmptyDraftsDesc:
    "All your listings are published. Create a new one to add more.",
  myListingsCreateFirst: "Create First Listing",
  myListingsPublished: "Published",
  myListingsDraft: "Draft",
  myListingsExpired: "Unpublished",
  myListingsEdit: "Edit listing",
  myListingsDelete: "Delete listing",
  myListingsDeleteTitle: "Delete Listing",
  myListingsDeleteMessage:
    "Are you sure you want to delete this listing? This action cannot be undone.",
  myListingsDeleteConfirm: "Delete",
  myListingsDeleted: "Listing deleted successfully",
  myListingsDeleteError: "Failed to delete listing",
  myListingsLoadError: "Failed to load listings",
  myListingsUnpublish: "Unpublish",
  myListingsRepublish: "Republish",
  myListingsPublishAction: "Publish",
  myListingsStatusUpdated: "Listing status updated",
  myListingsStatusUpdateError: "Failed to update listing status",

  // Settings Page
  settingsTitle: "Settings",
  settingsSubtitle: "Manage your account settings and preferences",
  settingsProfilePicture: "Profile Picture",
  settingsPhotoComingSoon: "Photo upload coming soon",
  settingsPersonalInfo: "Personal Information",
  settingsPhone: "Phone number",
  settingsEmailReadOnly: "Email cannot be changed",
  settingsOrganization: "Organization",
  settingsOrgReadOnly: "Contact support to change organization",
  settingsSecurity: "Security",
  settingsChangePasswordDesc:
    "Change your password by entering your current password and a new one",
  settingsCurrentPassword: "Current password",
  settingsNewPassword: "New password",
  settingsChangePassword: "Change Password",
  settingsPasswordChanged: "Password changed successfully",
  settingsCurrentPasswordWrong: "Current password is incorrect",
  settingsPasswordTooWeak: "New password is too weak",
  settingsReauthRequired:
    "Please sign out and sign in again before changing password",
  settingsPasswordChangeError: "Failed to change password. Please try again.",
  settingsSave: "Save Changes",
  settingsSaved: "Settings saved successfully",
  settingsSaveError: "Failed to save settings. Please try again.",
  settingsLoadError: "Failed to load settings",

  // Reset Password Page
  resetPasswordTitle: "Reset Your Password",
  resetPasswordSubtitle: "Enter a new password for your account:",
  resetPasswordNewPassword: "New Password",
  resetPasswordSuccessTitle: "Password Reset Complete",
  resetPasswordSuccessBody:
    "Your password has been successfully changed. You can now sign in with your new password.",
  resetPasswordMissingCodeTitle: "Missing Reset Code",
  resetPasswordMissingCodeBody:
    "No reset code was provided. Please click the link from your password reset email.",
  resetPasswordFailedTitle: "Password Reset Failed",
  resetPasswordExpired:
    "This password reset link has expired. Please request a new one from the login page.",
  resetPasswordInvalid:
    "This password reset link is invalid or has already been used.",
  resetPasswordFailed: "Unable to reset your password. Please try again later.",
  resetPasswordSubmit: "Reset Password",

  // Verification System
  verificationVerified: "Verified",
  verificationPending: "Verification Pending",
  verificationUnverified: "Unverified",
  verificationRejected: "Verification Rejected",
  verificationModalTitle: "Partner Verification",
  verificationStep1: "Registration Information",
  verificationStep2: "Document Upload (KYB)",
  verificationAccountType: "Account Type",
  verificationAccountTypeCorporate: "Corporate (法人 Hojin)",
  verificationAccountTypeIndividual: "Individual (個人 Kojin)",
  verificationAccountTypeHint:
    "Auto-filled from registration. Corporate for companies, Individual for sole proprietors.",
  verificationCompanyName: "Official Company Name",
  verificationCompanyNamePlaceholder: "e.g., Tokyo Real Estate Co., Ltd.",
  verificationCompanyNameHint:
    "The official legal name of your company or business.",
  verificationLicenseNumber: "Real Estate License Number",
  verificationLicenseNumberHint:
    "Format: [Prefecture] Governor (X) No. XXXXX (e.g., 東京都知事 (1) 第12345号)",
  verificationCorporateEmail: "Corporate Email Address",
  verificationCorporateEmailHint:
    "Must be a corporate email. Free providers (Gmail, Yahoo, etc.) are not allowed.",
  verificationOfficialWebsite: "Official Website (Optional)",
  verificationOfficialWebsiteHint:
    "Your company's official website URL for verification purposes.",
  verificationAgencyLicense: "Agency License (宅地建物取引業者免許証)",
  verificationAgencyLicenseHint:
    "Upload a clear photo or scan of your agency license document.",
  verificationOfficerId: "Responsible Officer ID (宅地建物取引士証)",
  verificationOfficerIdHint:
    "Upload a clear photo of the Takken-shi plastic card.",
  verificationUploadComingSoon:
    "File upload coming soon. Media storage integration pending.",
  verificationDocumentNote:
    "Document upload functionality will be available once media storage is integrated. You can submit the form now and upload documents later.",
  note: "Note",
  verificationStartButton: "Start Verification",
  verificationNext: "Next Step",
  verificationBack: "Back",
  verificationSubmit: "Submit for Review",
  verificationThankYou: "Thank You!",
  verificationThankYouMessage:
    "Your verification request has been submitted successfully. Our team will review your application and notify you of the decision. This typically takes 1-3 business days.",
  verificationRequiredTooltip:
    "Only verified real estate agents can publish listings. Please complete verification.",
  verificationRequiredMessage:
    "Your account must be verified before you can create listings. Please complete the verification process from your Profile page.",
  verificationRequests: "Verification Requests",
  verificationRequestsTitle: "Partner Verification Requests",
  verificationViewDetails: "View Details",
  verificationApprove: "Approve",
  verificationReject: "Reject",
  verificationRejectionReason: "Rejection Reason",
  verificationRejectionReasonPlaceholder:
    "e.g., License scan is blurry, please resubmit with a clearer image",
  verificationSubmitted: "Verification request submitted successfully",
  verificationSubmitError: "Failed to submit verification request",

  // Forgot Password Flow (on Login Page)
  forgotPassword: "Forgot password?",
  resetPasswordDesc:
    "Enter your email and we'll send you a link to reset your password.",
  resetPasswordSent:
    "If an account exists with that email, a reset link has been sent. Check your inbox.",
  sendResetLink: "Send Reset Link",
  enterYourEmail: "Enter your email address",

  // Layout Descriptions (for tooltips)
  layoutDescription1R: "Single room with kitchen area (no separate kitchen)",
  layoutDescription1K: "One room + separate Kitchen",
  layoutDescription1DK: "One room + Dining Kitchen (8+ tatami)",
  layoutDescription1LDK: "One room + Living Dining Kitchen (10+ tatami)",
  layoutDescription2K: "Two rooms + separate Kitchen",
  layoutDescription2DK: "Two rooms + Dining Kitchen",
  layoutDescription2LDK: "Two rooms + Living Dining Kitchen",
  layoutDescription3K: "Three rooms + separate Kitchen",
  layoutDescription3DK: "Three rooms + Dining Kitchen",
  layoutDescription3LDKPlus: "Three or more rooms + Living Dining Kitchen",

  // Photo Sections (for upload)
  photoSectionFloorPlan: "Floor Plan",
  photoSectionMainRoom: "Main Room",
  photoSectionKitchenArea: "Kitchen Area",
  photoSectionGenkan: "Entrance (Genkan)",
  photoSectionBedroom: "Bedroom",
  photoSectionDiningKitchen: "Dining Kitchen",
  photoSectionLivingRoom: "Living Room",
  photoSectionRoom1: "Room 1",
  photoSectionRoom2: "Room 2",
  photoSectionRoom3: "Room 3",
  photoSectionRoom3Plus: "Additional Rooms",
  photoUploadHint: "Drag & drop photos or click to browse",
  photoUploadMax: "Maximum 10 photos per section",

  // FileUpload Component
  fileUploadDragDrop: "Drag & drop files here or click to browse",
  fileUploadClick: "Click to upload",
  fileUploadUploading: "Uploading...",
  fileUploadError: "Upload failed",
  fileUploadInvalidType: "Invalid file type",
  fileUploadTooLarge: "File too large",
  fileUploadMaxFiles: "Maximum {max} files allowed",

  // PhotoGalleryCarousel Component
  photoGalleryScrollLeft: "Scroll categories left",
  photoGalleryScrollRight: "Scroll categories right",
  photoGalleryPreviousPhoto: "Previous photo",
  photoGalleryNextPhoto: "Next photo",
  photoGalleryNoImages: "No images available for this section",

  // Nearest Station Selector
  "listing.nearestStation.searching": "Searching nearby stations...",
  "listing.nearestStation.fetchError": "Failed to load nearby stations",
  "listing.nearestStation.searchError": "Failed to search stations",
  "listing.nearestStation.invalidAddress":
    "Invalid address. Please enter a valid Japanese address.",
  "listing.nearestStation.suggested": "Select your nearest stations:",
  "listing.nearestStation.selected": "Selected stations",
  "listing.nearestStation.minWalk": "min walk",
  "listing.nearestStation.notYourStations":
    "Not your stations? Search manually",
  "listing.nearestStation.autoSearch": "Find Nearby",
  "listing.nearestStation.manualSelect": "Search stations",
  "listing.nearestStation.searchPlaceholder": "Type station name...",
  "listing.nearestStation.noResults": "No stations found",
  "listing.nearestStation.tryAgain": "Try Again",
  "listing.nearestStation.enterAddress":
    "Enter address to find nearby stations",
  "listing.nearestStation.minCharsError": "Please enter at least 2 characters",
  "listing.nearestStation.loading": "Loading stations...",
  "listing.nearestStation.selectCityFirst": "Please select a city first",
  "listing.nearestStation.waitForGeocoding":
    "Please wait for address geocoding to complete, then try again",

  // Common
  cancel: "Cancel",
  "common.close": "Close",
  "common.search": "Search",
  "common.loading": "Loading...",
  "common.loadError": "Failed to load data",
  "common.noResults": "No results found",

  // Location Dropdown
  "listing.location.selectPrefecture": "Select prefecture",
  "listing.location.selectCity": "Select city",
  "listing.location.selectWard": "Select ward/district",
  "listing.location.selectPrefectureFirst": "Please select a prefecture first",
  "listing.location.selectCityFirst": "Please select a city first",
  "listing.location.noDataAvailable": "No data available",

  // Listing Detail Page
  listingDetailBackToSearch: "Back to search",
  listingDetailPropertyDetails: "Property Details",
  listingDetailDescription: "Description",
  listingDetailAmenities: "Amenities",
  listingDetailLocation: "Location",
  listingDetailNearbyStation: "Nearby Station",
  listingDetailListedBy: "Listed By",
  listingDetailContactAgent: "Contact Agent",
  listingDetailVerifiedPartner: "Verified Partner",
  listingDetailMonthShort: "mo",
  listingDetailManagementFee: "management fee",
  listingDetailBuilt: "Built",
  listingDetailRoomLayout: "Room Layout",
  listingDetailArea: "Area",
  listingDetailPricePerMonth: "/ month",
  expandFullscreen: "Fullscreen",
  listingDetailNearestStations: "Nearest Stations",
  listingDetailMinWalk: "min walk",
  listingDetailDeposit: "Deposit",
  listingDetailKeyMoney: "Key Money",
  listingDetailGuarantorRequired: "Guarantor Required",
  listingDetailYes: "Yes",
  listingDetailNo: "No",
  listingDetailNotSpecified: "Not specified",
  listingDetailNotFound: "Listing Not Found",
  listingDetailNotAvailable:
    "This listing is not available or has been removed.",
  listingDetailGoBack: "Go Back",
  listingDetailLoading: "Loading...",
  listingDetailStationWalk: "min walk",
  listingDetailStation: "Station",

  // Photo sections (for carousel display)
  detailPhotoExterior: "Exterior",
  detailPhotoInterior: "Interior",
  detailPhotoKitchen: "Kitchen",
  detailPhotoBathroom: "Bathroom",
  detailPhotoOther: "Other",

  // Amenities (from listingDetailAmenities)
  amenityAirConditioning: "Air conditioning",
  amenityWasherHookup: "Washer hookup",
  amenityWasherDryerInUnit: "Washer/dryer in-unit",
  amenityIndoorWasherSpace: "Indoor washer space",
  amenityBathToiletSeparate: "Bath/toilet separate",
  amenityReheatingBath: "Reheating bath",
  amenityBathroomDryer: "Bathroom dryer",
  amenityWashletSeat: "Washlet/bidet seat",
  amenityIndependentVanity: "Independent vanity",
  amenityGasStove: "Gas stove",
  amenityIhCooktop: "IH cooktop",
  amenitySystemKitchen: "System kitchen",
  amenityCounterKitchen: "Counter kitchen",
  amenityDishwasher: "Dishwasher",
  amenityBalcony: "Balcony",
  amenityFlooringWood: "Wood flooring",
  amenityElevator: "Elevator",
  amenitySouthFacing: "South-facing",
  amenityCornerUnit: "Corner unit",
  amenityAutoLock: "Auto-lock entrance",
  amenitySecurityCamera: "Security camera",
  amenityVideoIntercom: "Video intercom",
  amenityDeliveryBox: "Delivery box",
  amenityParking: "Parking available",
  amenityBicycleParking: "Bicycle parking",
  amenityMotorcycleParking: "Motorcycle parking",
  amenityInternetIncluded: "Internet included",
  amenityPetsAllowed: "Pets allowed",
  amenityInstrumentsOk: "Musical instruments OK",
  amenityForeignerFriendly: "Foreigner-friendly",
  amenitySohoOk: "SOHO/Office OK",

  // Legal Documents
  termsOfUse: "Terms of Use",
  partnerTerms: "Partner Terms of Service",
  privacyPolicy: "Privacy Policy",
  tokushoHo: "Tokusho-ho Notation",
  iAgreeToThe: "I have read and agree to the",
  termsAcceptanceRequired: "You must accept the terms",
  privacyAcceptanceRequired: "You must accept the privacy policy",
  legalAcceptanceRequired: "Please accept all required legal documents",

  // Listing Form - Missing Translations
  selectedStations: "Selected Stations",
  createListingAreaRequired: "Area (sqm) is required for publishing",
  createListingFloorRequired: "Floor is required for publishing",
  createListingTotalFloorsRequired: "Total floors is required for publishing",
  createListingYearBuiltRequired: "Year built is required for publishing",
  createListingFeaturedPhoto: "Featured Photo",
  createListingSetFeatured: "Set as featured",
  createListingRemoveFeatured: "Remove featured",
  createListingFeaturedPhotoHint:
    "Select a photo to display on overview cards. Floor plan shown by default if none selected.",
  photoSectionBathroom: "Bathroom",
  photoSectionKitchen: "Kitchen",
};

export const t = (key: keyof Locale, ...args: string[]) =>
  translate(en, key, ...args);
